import { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import Modal from '../Modal';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const DEFAULT_YAML = `name: my-workflow
description: My custom workflow

input:
  - name: companyId
    type: string
    required: true
    description: Company ID to process

steps:
  - id: step-1
    name: First Step
    type: check_new
    params:
      source: TED
    onFailure: fail
    maxRetries: 0

  # - id: step-2
  #   name: Second Step
  #   type: crawl_all
  #   dependsOn:
  #     - step-1
`;

const YAMLWorkflowModal = ({ isOpen, onClose, template, onSave, onLoadYAML }) => {
  const [yaml, setYaml] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingYAML, setLoadingYAML] = useState(false);
  const [error, setError] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [errorLine, setErrorLine] = useState(null);
  const editorRef = useRef(null);

  const isEditing = !!template;

  // Load YAML when editing an existing template
  useEffect(() => {
    if (isOpen && template && onLoadYAML) {
      loadTemplateYAML();
    } else if (isOpen && !template) {
      setYaml(DEFAULT_YAML);
      setError(null);
      setParseError(null);
      setErrorLine(null);
    }
  }, [isOpen, template]);

  const loadTemplateYAML = async () => {
    setLoadingYAML(true);
    setError(null);
    try {
      const result = await onLoadYAML(template.id);
      if (result?.success && result?.yaml) {
        setYaml(result.yaml);
      } else {
        setError(result?.error || 'Failed to load YAML');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingYAML(false);
    }
  };

  const handleEditorMount = (editor) => {
    editorRef.current = editor;
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setParseError(null);
    setErrorLine(null);

    try {
      const result = await onSave(yaml);

      if (result?.success) {
        onClose();
      } else {
        // Handle parse errors with line numbers
        if (result?.parseError) {
          setParseError(result.parseError);
          if (result?.lineNumber) {
            setErrorLine(result.lineNumber);
            // Highlight the error line in the editor
            if (editorRef.current) {
              editorRef.current.revealLineInCenter(result.lineNumber);
              editorRef.current.setSelection({
                startLineNumber: result.lineNumber,
                startColumn: 1,
                endLineNumber: result.lineNumber,
                endColumn: 1000,
              });
            }
          }
        } else {
          setError(result?.error || 'Failed to save workflow');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([yaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = template ? `${template.name}.yaml` : 'workflow.yaml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `EDIT: ${template.name.toUpperCase()}` : 'CREATE WORKFLOW'}
      size="xl"
    >
      <div className="space-y-4">
        {/* Info bar */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500 font-mono">
            {isEditing ? `Editing template v${template.version}` : 'Define your workflow in YAML format'}
          </p>
          <div className="flex items-center gap-2">
            {isEditing && (
              <button
                type="button"
                onClick={loadTemplateYAML}
                disabled={loadingYAML}
                className="px-2 py-1 bg-gray-800 text-gray-400 font-mono text-xs hover:bg-gray-700 flex items-center gap-1"
                title="Reload from server"
              >
                <ArrowPathIcon className={`w-3 h-3 ${loadingYAML ? 'animate-spin' : ''}`} />
                RELOAD
              </button>
            )}
            <button
              type="button"
              onClick={handleDownload}
              className="px-2 py-1 bg-gray-800 text-gray-400 font-mono text-xs hover:bg-gray-700 flex items-center gap-1"
              title="Download YAML"
            >
              <ArrowDownTrayIcon className="w-3 h-3" />
              DOWNLOAD
            </button>
          </div>
        </div>

        {/* Error display */}
        {(error || parseError) && (
          <div className="bg-red-50 border border-red-200 p-3">
            <p className="text-red-700 font-mono text-xs font-bold uppercase mb-1">
              {parseError ? 'YAML Parse Error' : 'Error'}
              {errorLine && ` (Line ${errorLine})`}
            </p>
            <p className="text-red-700 font-mono text-xs whitespace-pre-wrap">
              {parseError || error}
            </p>
          </div>
        )}

        {/* YAML Editor */}
        <div className="border border-gray-700 overflow-hidden" style={{ height: '400px' }}>
          {loadingYAML ? (
            <div className="h-full flex items-center justify-center bg-gray-900">
              <ArrowPathIcon className="w-6 h-6 text-gray-500 animate-spin" />
            </div>
          ) : (
            <Editor
              height="400px"
              language="yaml"
              theme="vs-dark"
              value={yaml}
              onChange={(value) => setYaml(value || '')}
              onMount={handleEditorMount}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                tabSize: 2,
                insertSpaces: true,
                automaticLayout: true,
                padding: { top: 10 },
              }}
            />
          )}
        </div>

        {/* Help text */}
        <div className="bg-gray-800 border border-gray-700 p-3">
          <p className="text-xs text-gray-400 font-mono mb-2">YAML Structure:</p>
          <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 font-mono">
            <div>
              <p className="text-gray-400 mb-1">Required fields:</p>
              <ul className="list-disc list-inside">
                <li>name - unique identifier</li>
                <li>steps - array of workflow steps</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-400 mb-1">Step types:</p>
              <ul className="list-disc list-inside">
                <li>check_new, crawl, crawl_all</li>
                <li>deep_crawl, convert_documents</li>
                <li>index_tender, assess_tender</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-gray-400 font-mono text-xs font-bold hover:bg-gray-700 uppercase"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || !yaml.trim()}
            className="px-4 py-2 bg-primary text-on-accent font-mono text-xs font-bold hover:brightness-110 disabled:opacity-50 uppercase"
          >
            {loading ? 'SAVING...' : 'SAVE WORKFLOW'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default YAMLWorkflowModal;
